import React from "react";
import { useQueries, useQueryClient} from "react-query";
import { useUserState } from "./UserContext";

import {
  getAdminUsers,
  getResellers,
  getEnterprises,
  clearCache,
} from "./cachedData";
import { STATIC_DATA_TYPES } from "../providers/RakutenSDK/RakutenSDK";

const CachedDataStateContext = React.createContext();
const CachedDataDispatchContext = React.createContext();

const requiredCachedDataKeys = ["adminList", "enterpriseList", "resellerList"];

//this hook fetches all of the static data for the application at once
const useGetCachedData = (cachedDataKeys) => {
  const queries = [];

  cachedDataKeys.forEach((cachedDataKey) => {
    let queryFn;
    switch (cachedDataKey) {
      case "adminList": {
        queryFn = getAdminUsers;
        break;
      }
      case "enterpriseList": {
        queryFn = getEnterprises;
        break;
      }
      case "resellerList": {
        queryFn = getResellers;
        break;
      }
      default:
        break;
    }

    queries.push({
      queryKey: [cachedDataKey],
      queryFn: () => queryFn(),
    });
  });

  const cachedData = useQueries(queries);
  const results = {};
  cachedData.forEach((currentItem, index) => {
    results[cachedDataKeys[index]] = currentItem.data || [];
  });

  if (Object.entries(results).length !== 0) return results;
};

const getCachedKey = (user_details = {}) => {
  const { enterprise_id, reseller_id } = user_details;

  if (enterprise_id) {
    return ["enterpriseList"];
  } else if (reseller_id) {
    return ["enterpriseList", "resellerList"];
  }

  return requiredCachedDataKeys;
};

const CachedDataProvider = ({ children }) => {

  const queryClient = useQueryClient()
  function cachedDataReducer(state, action) {
    if (action.type === "RESET_ENTERPRISE_LIST") {
      clearCache(STATIC_DATA_TYPES.ENTERPRISE_LIST);
      queryClient.refetchQueries(["enterpriseList"], { exact: true })
    } else {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
  // eslint-disable-next-line no-unused-vars
  const [state, dispatch] = React.useReducer(cachedDataReducer);
  const { isAuthenticated, user_details } = useUserState();

  const initialState =
    useGetCachedData(isAuthenticated ? getCachedKey(user_details) : []) || {};

  return (
    <CachedDataStateContext.Provider value={initialState}>
      <CachedDataDispatchContext.Provider value={dispatch}>
        {children}
      </CachedDataDispatchContext.Provider>
    </CachedDataStateContext.Provider>
  );
};

const useCachedData = () => {
  const context = React.useContext(CachedDataStateContext);

  if (context === undefined) {
    throw new Error("CachedDataState must be used within a CachedDataProvider");
  }
  return context;
};

function useCachedDataDispatch() {
  var context = React.useContext(CachedDataDispatchContext);
  if (context === undefined) {
    throw new Error(
      "useCachedDataDispatch must be used within a CachedDataProvider"
    );
  }
  return context;
}

export { CachedDataProvider, useCachedData, useCachedDataDispatch };
